import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Input, Alert, Badge } from 'reactstrap';
import ConditionalNavBar from '../components/ConditionalNavBar';
import axiosInstance from '../api/Axios';

const PropertyBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get property and booking data from navigation state
  const { property, searchParams, bookingDates, matchingRoomIds } = location.state || {};
  
  const [bookingData, setBookingData] = useState({
    checkIn: bookingDates?.checkIn || '',
    checkOut: bookingDates?.checkOut || '',
    voucher_code: '',
    special_requests: '',
    matching_room_ids: matchingRoomIds || [] // Store matching room IDs
  });
  
  const [voucherValidation, setVoucherValidation] = useState({
    isValid: null,
    message: '',
    loading: false,
    voucherData: null // Store voucher data for booking
  });
  
  const [booking, setBooking] = useState({
    loading: false,
    success: false,
    error: ''
  });

  // Ref to prevent infinite validation loops
  const isValidatingRef = useRef(false);
  const lastValidatedDatesRef = useRef({ checkIn: '', checkOut: '', voucher: '' });

  // Redirect if no property data
  useEffect(() => {
    if (!property) {
      navigate('/property-search-results');
    }
  }, [property, navigate]);



  // Calculate booking duration and total cost based on daily pricing
  const calculateBookingDetails = () => {
    if (!bookingData.checkIn || !bookingData.checkOut) return null;
    
    const checkInDate = new Date(bookingData.checkIn);
    const checkOutDate = new Date(bookingData.checkOut);
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Use daily pricing if available, fallback to monthly calculation
    const dailyPrice = property.price_per_day || (property.price_per_month / 30);
    const totalCost = dailyPrice * diffDays;
    
    return {
      days: diffDays,
      dailyPrice: dailyPrice,
      totalCost: totalCost
    };
  };

  const bookingDetails = calculateBookingDetails();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateVoucher = useCallback(async () => {
    if (!bookingData.voucher_code.trim()) {
      setVoucherValidation({
        isValid: false,
        message: 'Please enter a voucher code',
        loading: false
      });
      return;
    }

    // Prevent concurrent validations
    if (isValidatingRef.current) {
      console.log('🔄 Validation already in progress, skipping...');
      return;
    }

    isValidatingRef.current = true;
    setVoucherValidation({ isValid: null, message: '', loading: true });

    try {
      console.log('🔍 VOUCHER VALIDATION REQUEST:', {
        url: '/api/accounts/validate-voucher/',
        voucher_code: bookingData.voucher_code.trim(),
        check_in_date: bookingData.checkIn,
        check_out_date: bookingData.checkOut,
        property_id: property?.id,
        timestamp: new Date().toISOString()
      });

      // Build request data with booking dates if available
      const requestData = {
        voucher_code: bookingData.voucher_code.trim()
      };

      // Add query parameters for date and cost validation
      const queryParams = new URLSearchParams();
      if (bookingData.checkIn) {
        queryParams.append('check_in_date', bookingData.checkIn);
      }
      if (bookingData.checkOut) {
        queryParams.append('check_out_date', bookingData.checkOut);
      }
      if (property?.id) {
        queryParams.append('property_id', property.id);
      }

      const url = `/api/accounts/validate-voucher/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await axiosInstance.post(url, requestData);

      console.log('✅ VOUCHER VALIDATION RESPONSE:', {
        status: response.status,
        data: response.data,
        timestamp: new Date().toISOString()
      });

      if (response.data.voucher) {
        console.log('📋 VOUCHER DETAILS:', {
          id: response.data.voucher.id,
          code: response.data.voucher.code,
          amount: response.data.voucher.amount,
          currency: response.data.voucher.currency,
          valid_from: response.data.voucher.valid_from,
          valid_until: response.data.voucher.valid_until,
          days_remaining: response.data.voucher.days_remaining
        });
      }

      if (response.data.date_constraint) {
        console.log('📅 DATE CONSTRAINTS:', response.data.date_constraint);
      }

      console.log('🎯 VALIDATION RESULT:', {
        isValid: response.data.valid,
        message: response.data.message,
        hasVoucher: !!response.data.voucher,
        hasDateConstraint: !!response.data.date_constraint
      });

      if (response.data.valid) {
        setVoucherValidation({
          isValid: true,
          message: response.data.message || 'Voucher is valid! You can proceed with booking.',
          loading: false,
          voucherData: response.data.voucher // Store voucher data
        });
      } else {
        setVoucherValidation({
          isValid: false,
          message: response.data.message || 'This voucher is not valid for your account.',
          loading: false,
          voucherData: null
        });
      }
      isValidatingRef.current = false;
    } catch (error) {
      console.error('❌ VOUCHER VALIDATION ERROR:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        timestamp: new Date().toISOString()
      });
      setVoucherValidation({
        isValid: false,
        message: error.response?.data?.error || 'Error validating voucher. Please try again.',
        loading: false,
        voucherData: null
      });
      isValidatingRef.current = false;
    }
  }, [bookingData.voucher_code, bookingData.checkIn, bookingData.checkOut, property?.id]);

  // Re-validate voucher when booking dates change (if voucher code exists)
  useEffect(() => {
    const currentDates = {
      checkIn: bookingData.checkIn,
      checkOut: bookingData.checkOut,
      voucher: bookingData.voucher_code
    };
    
    // Only validate if:
    // 1. We have a voucher code
    // 2. We have both dates
    // 3. We're not already validating
    // 4. The dates or voucher have actually changed
    const shouldValidate = 
      bookingData.voucher_code.trim() && 
      bookingData.checkIn && 
      bookingData.checkOut &&
      !isValidatingRef.current &&
      (
        lastValidatedDatesRef.current.checkIn !== currentDates.checkIn ||
        lastValidatedDatesRef.current.checkOut !== currentDates.checkOut ||
        lastValidatedDatesRef.current.voucher !== currentDates.voucher
      );

    if (shouldValidate) {
      console.log('🔄 RE-VALIDATING voucher due to date change:', currentDates);
      lastValidatedDatesRef.current = currentDates;
      validateVoucher();
    }
  }, [bookingData.checkIn, bookingData.checkOut, bookingData.voucher_code, validateVoucher]);

  const handleBooking = async (e) => {
    e.preventDefault();
    
    // Require voucher for booking
    if (!bookingData.voucher_code || !voucherValidation.isValid) {
      setBooking({
        loading: false,
        success: false,
        error: 'Please enter and validate a voucher code before booking.'
      });
      return;
    }

    setBooking({ loading: true, success: false, error: '' });

    try {
      // Use voucher amount as the total price (full voucher value)
      const totalPrice = voucherValidation.voucherData?.amount || 0;
      
      // Create booking through the VoucherBooking API
      const bookingPayload = {
        accommodation_property: property.id,
        check_in_date: bookingData.checkIn,
        check_out_date: bookingData.checkOut,
        special_requests: bookingData.special_requests || '',
        matching_room_ids: bookingData.matching_room_ids, // Include matching room IDs
        total_price: totalPrice // Use full voucher amount as total price
      };
      
      console.log('DEBUG: Property ID being sent:', property.id);
      console.log('DEBUG: Matching room IDs:', bookingData.matching_room_ids);
      console.log('DEBUG: Total Price (Voucher Amount):', totalPrice);
      console.log('DEBUG: Full booking payload:', bookingPayload);

      // Only include voucher if one was provided and validated
      if (bookingData.voucher_code && voucherValidation.isValid) {
        try {
          console.log('🔍 BOOKING VOUCHER VALIDATION REQUEST:', {
            url: '/api/accounts/validate-voucher/',
            voucher_code: bookingData.voucher_code,
            context: 'booking_submission',
            timestamp: new Date().toISOString()
          });

          // Use the validation endpoint to get voucher details since we know it works
          const voucherValidateResponse = await axiosInstance.post('/api/accounts/validate-voucher/', {
            voucher_code: bookingData.voucher_code
          });
          
          console.log('✅ BOOKING VOUCHER VALIDATION RESPONSE:', {
            status: voucherValidateResponse.status,
            data: voucherValidateResponse.data,
            timestamp: new Date().toISOString()
          });

          if (voucherValidateResponse.data.voucher) {
            console.log('📋 BOOKING VOUCHER DETAILS:', {
              id: voucherValidateResponse.data.voucher.id,
              code: voucherValidateResponse.data.voucher.code,
              amount: voucherValidateResponse.data.voucher.amount,
              currency: voucherValidateResponse.data.voucher.currency,
              valid_from: voucherValidateResponse.data.voucher.valid_from,
              valid_until: voucherValidateResponse.data.voucher.valid_until,
              days_remaining: voucherValidateResponse.data.voucher.days_remaining
            });
          }

          if (voucherValidateResponse.data.date_constraint) {
            console.log('📅 BOOKING DATE CONSTRAINTS:', voucherValidateResponse.data.date_constraint);
          }

          console.log('🎯 BOOKING VALIDATION RESULT:', {
            isValid: voucherValidateResponse.data.valid,
            message: voucherValidateResponse.data.message,
            hasVoucher: !!voucherValidateResponse.data.voucher,
            hasDateConstraint: !!voucherValidateResponse.data.date_constraint,
            context: 'booking_submission'
          });
          
          if (voucherValidateResponse.data.valid && voucherValidateResponse.data.voucher) {
            bookingPayload.voucher = voucherValidateResponse.data.voucher.id;
            console.log('✅ Found voucher ID from validation:', bookingPayload.voucher);
          } else {
            throw new Error('Voucher validation failed during booking');
          }
        } catch (voucherError) {
          console.error('❌ BOOKING VOUCHER VALIDATION ERROR:', {
            error: voucherError.message,
            response: voucherError.response?.data,
            status: voucherError.response?.status,
            context: 'booking_submission',
            timestamp: new Date().toISOString()
          });
          throw new Error(`Failed to get voucher for booking: ${voucherError.message}`);
        }
      }

      console.log('Creating booking with payload:', bookingPayload);
      console.log('Property ID:', property.id);
      console.log('Voucher validation state:', voucherValidation);
      
      const response = await axiosInstance.post('/api/accounts/voucher-bookings/', bookingPayload);
      
      console.log('Booking created successfully:', response.data);
      
      setBooking({
        loading: false,
        success: true,
        error: '',
        bookingId: response.data.id,
        bookingDetails: {
          room_number: response.data.room_number,
          bed_number: response.data.bed_number,
          block_number: response.data.block_number,
          floor_number: response.data.floor_number
        }
      });

    } catch (error) {
      console.error('Error creating booking:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'Failed to create booking. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle various error formats
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.voucher) {
          errorMessage = Array.isArray(errorData.voucher) 
            ? errorData.voucher[0] 
            : errorData.voucher;
        } else if (errorData.accommodation_property) {
          errorMessage = Array.isArray(errorData.accommodation_property) 
            ? errorData.accommodation_property[0] 
            : errorData.accommodation_property;
        } else if (errorData.booking_dates) {
          errorMessage = errorData.booking_dates;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors[0] 
            : errorData.non_field_errors;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          // Show all errors if multiple field errors
          const errorFields = Object.keys(errorData);
          if (errorFields.length > 0) {
            errorMessage = `Error in ${errorFields.join(', ')}: ${JSON.stringify(errorData)}`;
          }
        }
      } else if (error.response?.status === 401) {
        errorMessage = 'Please log in to create a booking.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to create bookings.';
      }
      
      setBooking({
        loading: false,
        success: false,
        error: errorMessage
      });
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  if (!property) {
    return <div>Loading...</div>;
  }

  if (booking.success) {
    return (
      <div>
        <ConditionalNavBar />
        <Container className="my-5">
          <Row className="justify-content-center">
            <Col md="8">
              <Card>
                <CardBody className="text-center py-5">
                  <i className="fas fa-check-circle fa-5x text-success mb-4"></i>
                  <h2 className="text-success mb-3">Application Submitted!</h2>
                  <p className="lead mb-4">
                    Your accommodation application has been successfully submitted and is pending BMO approval.
                  </p>
                  <div className="alert alert-warning mb-4">
                    <i className="fas fa-clock me-2"></i>
                    <strong>Status:</strong> Pending BMO Approval
                  </div>
                  <div className="mb-4 text-start">
                    <strong>Property:</strong> {property.title}<br/>
                    <strong>Check-in:</strong> {bookingData.checkIn}<br/>
                    <strong>Check-out:</strong> {bookingData.checkOut}<br/>
                    {bookingData.voucher_code && (
                      <><strong>Voucher:</strong> {bookingData.voucher_code}<br/></>
                    )}
                  </div>
                  <div className="d-flex gap-3 justify-content-center">
                    <Button color="primary" onClick={() => navigate('/property-search-results')}>
                      Search More Properties
                    </Button>
                    <Button color="outline-primary" onClick={() => navigate('/')}>
                      Back to Home
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <ConditionalNavBar />
      
      <Container className="my-5">
        <Row>
          <Col md="8">
            {/* Property Details */}
            <Card className="mb-4">
              <CardBody>
                <Row>
                  <Col md="4">
                    <img
                      src={`https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=300&h=200&fit=crop&auto=format`}
                      alt={property.title}
                      className="img-fluid rounded"
                    />
                  </Col>
                  <Col md="8">
                    <h3 className="mb-2">{property.title}</h3>
                    <p className="text-muted mb-3">{property.description}</p>
                    
                    <div className="mb-3">
                      <Badge color="info" className="me-2">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {property.address.suburb}, {property.address.city}
                      </Badge>
                      <Badge color="light" className="me-2">
                        <i className="fas fa-bed me-1"></i>
                        {property.accommodation_details.room_types.join(', ')} rooms
                      </Badge>
                      <Badge color="light">
                        <i className="fas fa-users me-1"></i>
                        {property.accommodation_details.gender_preference.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="mb-3">
                      <h4 className="text-primary mb-0">
                        {formatPrice(property.price_per_day || (property.price_per_month / 30))}
                      </h4>
                      <small className="text-muted">per day</small>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Booking Form */}
            <Card>
              <CardBody>
                <h4 className="mb-4">Complete Your Booking</h4>
                
                <Form onSubmit={handleBooking}>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="checkIn">Check-in Date</Label>
                        <Input
                          type="date"
                          id="checkIn"
                          name="checkIn"
                          value={bookingData.checkIn}
                          onChange={handleInputChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="checkOut">Check-out Date</Label>
                        <Input
                          type="date"
                          id="checkOut"
                          name="checkOut"
                          value={bookingData.checkOut}
                          onChange={handleInputChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label for="voucher_code">Accommodation Voucher Code (Required)</Label>
                    <div className="d-flex gap-2">
                      <Input
                        type="text"
                        id="voucher_code"
                        name="voucher_code"
                        placeholder="Enter voucher code (e.g., 04260106091800000001)"
                        value={bookingData.voucher_code}
                        onChange={handleInputChange}
                      />
                      <Button
                        type="button"
                        color="outline-primary"
                        onClick={validateVoucher}
                        disabled={voucherValidation.loading || !bookingData.voucher_code.trim()}
                      >
                        {voucherValidation.loading ? 'Validating...' : 'Validate'}
                      </Button>
                    </div>
                    
                    {voucherValidation.message && (
                      <Alert 
                        color={voucherValidation.isValid ? 'success' : 'danger'} 
                        className="mt-2 mb-0"
                      >
                        <i className={`fas ${voucherValidation.isValid ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                        {voucherValidation.message}
                      </Alert>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="special_requests">Special Requests (Optional)</Label>
                    <Input
                      type="textarea"
                      id="special_requests"
                      name="special_requests"
                      placeholder="Any special requests or requirements..."
                      value={bookingData.special_requests}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </FormGroup>

                  {booking.error && (
                    <Alert color="danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {booking.error}
                    </Alert>
                  )}

                  <div className="d-flex gap-3 justify-content-end">
                    <Button 
                      type="button" 
                      color="outline-secondary" 
                      onClick={() => navigate('/property-search-results')}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Results
                    </Button>
                    <Button 
                      type="submit" 
                      color="success"
                      disabled={booking.loading}
                    >
                      {booking.loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2"></i>
                          Confirm Booking
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>

          {/* Booking Summary */}
          <Col md="4">
            <Card>
              <CardBody>
                <h5 className="mb-3">Booking Summary</h5>
                
                <div className="mb-3">
                  <strong>Property:</strong><br/>
                  <span className="text-muted">{property.title}</span>
                </div>
                
                {bookingDetails && (
                  <>
                    <div className="mb-3">
                      <strong>Duration:</strong><br/>
                      <span className="text-muted">{bookingDetails.days} days</span>
                    </div>
                  </>
                )}
                
                {bookingData.voucher_code && voucherValidation.isValid && (
                  <Alert color="success" className="small">
                    <i className="fas fa-ticket-alt me-2"></i>
                    Voucher applied: {bookingData.voucher_code}
                  </Alert>
                )}
                
                <div className="small text-muted">
                  <i className="fas fa-info-circle me-2"></i>
                  Final payment details will be confirmed after booking.
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PropertyBookingPage;